import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Table, Button, Space, Input, Select, DatePicker, InputNumber, Modal, Form,
  Popconfirm, message, Tag, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined,
  ThunderboltOutlined, CheckOutlined, CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFinanceApi, DOVIZ_OPTIONS, entityOptions } from '../api/financeApi';
import { enhanceColumns, toplamGenislik } from '../../ortak/tableHelpers';
import EditableCell from './EditableCell';
import { formatMoney, inputNumberMoneyProps } from '../../../utils/currency';

const { RangePicker } = DatePicker;

const fmtDate = (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '');

/**
 * Config-driven CRUD table for the simple Finans resources.
 *
 * Props:
 *  - resource (string)         backend /records/{resource}
 *  - title (string)
 *  - columns (array)           column defs (see config files)
 *  - lookups (object)          combined dropdown data
 *  - perms ({view,create,edit,delete})
 *  - filters (array)           subset of ['date','firma','doviz','active','search']
 *  - rowActions (array)        [{label, icon, danger, show(record), onClick(record)}]
 *  - refreshKey (number)       bump to force reload from parent
 *  - defaultFilters (object)   forced filters merged into every request (e.g. {odeme:1})
 */
export default function FinansDataTable({
  resource, title, columns, lookups, perms = {}, filters = ['date', 'firma', 'doviz', 'search'],
  rowActions = [], refreshKey = 0, defaultFilters = {},
}) {
  const api = useFinanceApi();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterState, setFilterState] = useState({});
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  // Satır içi (Excel benzeri) düzenleme durumu
  const [editingCell, setEditingCell] = useState(null); // { rowId, key }
  const [draftRow, setDraftRow] = useState(null); // { __draft: true, ...values }

  const can = {
    view: perms.view !== false,
    create: perms.create !== false,
    edit: perms.edit !== false,
    delete: perms.delete !== false,
  };

  const buildParams = useCallback(() => {
    const p = { ...defaultFilters };
    if (filterState.tarih_start) p.tarih_start = filterState.tarih_start;
    if (filterState.tarih_end) p.tarih_end = filterState.tarih_end;
    if (filterState.firma_id) p.firma_id = filterState.firma_id;
    if (filterState.doviz) p.doviz = filterState.doviz;
    if (filterState.active !== undefined && filterState.active !== null) p.active = filterState.active;
    if (search) p.genel_arama = search;
    return p;
  }, [filterState, search, JSON.stringify(defaultFilters)]);

  const load = useCallback(async () => {
    if (!can.view) return;
    setLoading(true);
    try {
      const rows = await api.list(resource, buildParams());
      setData(Array.isArray(rows) ? rows : []);
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Liste yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [resource, buildParams]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // debounced search
  const onSearchChange = (val) => {
    setSearch(val);
  };
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { load(); }, 300);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    const init = {};
    columns.forEach((c) => {
      if (c.inForm === false) return;
      if (c.type === 'date') init[c.key] = dayjs();
      else if (c.defaultValue !== undefined) init[c.key] = c.defaultValue;
    });
    form.setFieldsValue(init);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    const vals = {};
    columns.forEach((c) => {
      if (c.inForm === false) return;
      const raw = record[c.key];
      vals[c.key] = c.type === 'date' ? (raw ? dayjs(raw) : null) : raw;
    });
    form.setFieldsValue(vals);
    setModalOpen(true);
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = {};
    columns.forEach((c) => {
      if (c.inForm === false) return;
      let v = values[c.key];
      if (c.type === 'date' && v) v = dayjs(v).format('YYYY-MM-DD');
      payload[c.key] = v === undefined ? null : v;
    });
    try {
      if (editing) {
        await api.update(resource, editing.id, payload);
        message.success('Güncellendi');
      } else {
        await api.create(resource, payload);
        message.success('Eklendi');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kayıt başarısız');
    }
  };

  const doDelete = async (record) => {
    try {
      await api.remove(resource, record.id);
      message.success('Silindi');
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Silinemedi');
    }
  };

  // ---- Satır içi hücre düzenleme -------------------------------------------
  // Kural: c.editable === true → düzenlenebilir; c.editable === false → asla;
  // c.editable tanımsız → tipi INLINE_TYPES içindeyse düzenlenebilir. 'entity'
  // (cari/firma/işlem türü...) hücreleri de dropdown ile yerinde düzenlenir;
  // kayıt sonrası join'li görünen ad saveCell içinde load() ile tazelenir.
  const INLINE_TYPES = ['amount', 'number', 'date', 'select', 'doviz', 'text', 'textarea', 'entity'];
  const isInlineEditable = (c) => {
    if (c.editable === true) return true;
    if (c.editable !== undefined) return false;
    return INLINE_TYPES.includes(c.type);
  };

  const saveCell = async (record, c, value) => {
    setEditingCell(null);
    const formatted = value === undefined ? null : value; // tarih 'YYYY-MM-DD', sayılar number gelir
    const oldVal = record[c.key] === undefined ? null : record[c.key];
    if (formatted === oldVal) return; // değişiklik yok
    try {
      await api.update(resource, record.id, { [c.key]: formatted });
      if (c.type === 'entity' || c.display) {
        // join'li görünen ad değişti → tam yenile
        load();
      } else {
        setData((prev) => prev.map((r) => (r.id === record.id ? { ...r, [c.key]: formatted } : r)));
      }
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Güncellenemedi');
      // başarısızlıkta local veri değişmediği için görünüm eski değere döner
    }
  };

  // ---- Hızlı Ekle (taslak satır) -------------------------------------------
  const startDraft = () => {
    setEditingCell(null);
    setDraftRow((prev) => {
      if (prev) return prev; // aynı anda tek taslak
      const d = { __draft: true };
      columns.forEach((c) => {
        if (c.inForm === false) return;
        if (c.type === 'date') d[c.key] = dayjs().format('YYYY-MM-DD');
        else if (c.defaultValue !== undefined) d[c.key] = c.defaultValue;
      });
      return d;
    });
  };

  const saveDraftCell = (c, value) => {
    setDraftRow((prev) => (prev ? { ...prev, [c.key]: value === undefined ? null : value } : prev));
  };

  const commitDraft = async () => {
    if (!draftRow) return;
    const missing = columns.filter(
      (c) => c.inForm !== false && c.required
        && (draftRow[c.key] === undefined || draftRow[c.key] === null || draftRow[c.key] === ''),
    );
    if (missing.length) {
      message.warning(`Zorunlu alanlar eksik: ${missing.map((c) => c.title).join(', ')}`);
      return;
    }
    const payload = {};
    columns.forEach((c) => {
      if (c.inForm === false) return;
      const v = draftRow[c.key];
      payload[c.key] = v === undefined ? null : v;
    });
    try {
      await api.create(resource, payload);
      message.success('Eklendi');
      setDraftRow(null);
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kayıt başarısız');
    }
  };

  const tableColumns = useMemo(() => {
    const cols = columns
      .filter((c) => c.inTable !== false)
      .map((c) => {
        const colEditable = can.edit && c.inForm !== false && isInlineEditable(c);
        return {
          title: c.title,
          dataIndex: c.display || c.key,
          key: c.key,
          width: c.width,
          ellipsis: true,
          render: (val, record) => {
            if (c.render) return c.render(val, record);
            if (c.type === 'amount') {
              return <span style={{ whiteSpace: 'nowrap' }}>{formatMoney(record[c.key], record.doviz)}</span>;
            }
            if (c.type === 'date') return fmtDate(record[c.key]);
            if (c.type === 'select' && c.options) {
              const opt = c.options.find((o) => String(o.value) === String(record[c.key]));
              return opt ? <Tag>{opt.label}</Tag> : record[c.key];
            }
            return val;
          },
          onCell: (record) => ({
            record,
            colDef: c,
            dataIndex: c.key,
            lookups,
            editable: colEditable && !record.__draft,
            editing: record.__draft
              ? c.inForm !== false // taslak satırda tüm form kolonları aynı anda açık
              : !!editingCell && editingCell.rowId === record.id && editingCell.key === c.key,
            onStartEdit: () => setEditingCell({ rowId: record.id, key: c.key }),
            onSave: (value) => (record.__draft ? saveDraftCell(c, value) : saveCell(record, c, value)),
            onCancel: () => { if (!record.__draft) setEditingCell(null); },
          }),
        };
      });

    const actionButtons = (record) => {
      if (record.__draft) {
        return (
          <Space size={4}>
            <Tooltip title="Kaydet">
              <Button size="small" type="primary" icon={<CheckOutlined />} onClick={commitDraft} />
            </Tooltip>
            <Tooltip title="Vazgeç">
              <Button size="small" icon={<CloseOutlined />} onClick={() => setDraftRow(null)} />
            </Tooltip>
          </Space>
        );
      }
      return (
        <Space size={4}>
          {rowActions
            .filter((a) => (a.show ? a.show(record) : true))
            .map((a) => (
              <Tooltip title={a.label} key={a.label}>
                <Button size="small" danger={a.danger} icon={a.icon} onClick={() => a.onClick(record)}>
                  {a.icon ? null : a.label}
                </Button>
              </Tooltip>
            ))}
          {can.edit && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          )}
          {can.delete && (
            <Popconfirm title="Bu kayıt silinsin mi?" okText="Sil" cancelText="Vazgeç" onConfirm={() => doDelete(record)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      );
    };

    if (can.edit || can.delete || can.create || rowActions.length) {
      cols.push({
        title: 'İşlem', key: '__actions', fixed: 'right', width: 60 + rowActions.length * 70,
        render: (_, record) => actionButtons(record),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, rowActions, can.edit, can.delete, can.create, editingCell, draftRow, lookups, load]);

  if (!can.view) {
    return <div style={{ padding: 24 }}>Bu sekme için görüntüleme yetkiniz yok.</div>;
  }

  const formItem = (c) => {
    if (c.inForm === false) return null;
    let input;
    if (c.type === 'entity') {
      input = (
        <Select
          showSearch optionFilterProp="label" allowClear placeholder={c.title}
          options={entityOptions(lookups, c.entity, c.entityFilter)}
        />
      );
    } else if (c.type === 'select') {
      input = <Select allowClear options={c.options} placeholder={c.title} />;
    } else if (c.type === 'doviz') {
      input = <Select options={DOVIZ_OPTIONS} placeholder="Döviz" />;
    } else if (c.type === 'amount') {
      input = <InputNumber {...inputNumberMoneyProps} style={{ width: '100%' }} step={0.01} />;
    } else if (c.type === 'number') {
      input = <InputNumber style={{ width: '100%' }} step={1} />;
    } else if (c.type === 'date') {
      input = <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />;
    } else if (c.type === 'textarea') {
      input = <Input.TextArea rows={2} maxLength={c.max} />;
    } else {
      input = <Input maxLength={c.max} />;
    }
    return (
      <Form.Item
        key={c.key} name={c.key} label={c.title}
        rules={c.required ? [{ required: true, message: `${c.title} gerekli` }] : []}
      >
        {input}
      </Form.Item>
    );
  };

  return (
    <div className="finans-data-table">
      <Space wrap style={{ marginBottom: 12 }}>
        {filters.includes('date') && (
          <RangePicker
            format="DD.MM.YYYY"
            onChange={(vals) =>
              setFilterState((s) => ({
                ...s,
                tarih_start: vals && vals[0] ? vals[0].format('YYYY-MM-DD') : null,
                tarih_end: vals && vals[1] ? vals[1].format('YYYY-MM-DD') : null,
              }))
            }
          />
        )}
        {filters.includes('firma') && (
          <Select
            allowClear showSearch optionFilterProp="label" placeholder="Firma" style={{ width: 180 }}
            options={entityOptions(lookups, 'firma')}
            onChange={(v) => setFilterState((s) => ({ ...s, firma_id: v }))}
          />
        )}
        {filters.includes('doviz') && (
          <Select
            allowClear placeholder="Döviz" style={{ width: 110 }} options={DOVIZ_OPTIONS}
            onChange={(v) => setFilterState((s) => ({ ...s, doviz: v }))}
          />
        )}
        {filters.includes('active') && (
          <Select
            allowClear placeholder="Durum" style={{ width: 120 }}
            options={[{ value: 1, label: 'Aktif' }, { value: 0, label: 'Pasif' }]}
            onChange={(v) => setFilterState((s) => ({ ...s, active: v }))}
          />
        )}
        {filters.includes('search') && (
          <Input
            allowClear prefix={<SearchOutlined />} placeholder="Ara..." style={{ width: 220 }}
            value={search} onChange={(e) => onSearchChange(e.target.value)}
          />
        )}
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        {can.create && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni</Button>
        )}
        {can.create && (
          <Button icon={<ThunderboltOutlined />} onClick={startDraft} disabled={!!draftRow}>
            Hızlı Ekle
          </Button>
        )}
        <span style={{ color: '#888' }}>{data.length} kayıt</span>
      </Space>

      <Table
        rowKey={(r) => (r.__draft ? '__draft_id' : r.id)}
        size="small"
        loading={loading}
        dataSource={draftRow ? [draftRow, ...data] : data}
        columns={enhanceColumns(tableColumns)}
        components={{ body: { cell: EditableCell } }}
        scroll={{ x: toplamGenislik(enhanceColumns(tableColumns)), y: 480 }}
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} kayıt` }}
      />

      <Modal
        title={editing ? `${title} — Düzenle` : `${title} — Yeni Kayıt`}
        open={modalOpen}
        onOk={submit}
        onCancel={() => setModalOpen(false)}
        okText="Kaydet"
        cancelText="Vazgeç"
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {columns.map((c) => formItem(c))}
        </Form>
      </Modal>
    </div>
  );
}
